import { Controller } from "@hotwired/stimulus";

/**
 * SDK Switcher Controller
 *
 * Handles switching between different SDK code examples (JavaScript, Python, cURL).
 * Shows/hides code blocks and updates tab styling based on selection.
 * Uses smooth height animation via grid-template-rows for seamless transitions.
 */
export default class extends Controller<HTMLElement> {
  static targets = ["tab", "codeBlock", "container"];

  declare readonly tabTargets: HTMLElement[];
  declare readonly codeBlockTargets: HTMLElement[];
  declare readonly hasContainerTarget: boolean;
  declare readonly containerTarget: HTMLElement;

  private currentSDK: string = "javascript";
  private isAnimating: boolean = false;

  connect(): void {
    // Ensure initial state is correct
    this.updateDisplay("javascript", false);
  }

  switch(event: Event): void {
    const target = event.currentTarget as HTMLElement;
    const sdk = target.dataset.sdk;

    if (!sdk || sdk === this.currentSDK || this.isAnimating) return;

    this.updateDisplay(sdk, true);
  }

  private updateDisplay(activeSDK: string, animate: boolean = true): void {
    // Update tabs
    this.tabTargets.forEach((tab) => {
      const isActive = tab.dataset.sdk === activeSDK;

      if (isActive) {
        tab.classList.add("bg-base-content/10", "text-base-content", "shadow-sm");
        tab.classList.remove("text-base-content/50", "hover:text-base-content/80", "hover:bg-base-content/5");
      } else {
        tab.classList.remove("bg-base-content/10", "text-base-content", "shadow-sm");
        tab.classList.add("text-base-content/50", "hover:text-base-content/80", "hover:bg-base-content/5");
      }
    });

    const outgoingBlock = this.codeBlockTargets.find((b) => b.dataset.sdk === this.currentSDK);
    const incomingBlock = this.codeBlockTargets.find((b) => b.dataset.sdk === activeSDK);

    if (!incomingBlock) return;

    if (!animate || !outgoingBlock) {
      // Initial load - no animation
      this.codeBlockTargets.forEach((block) => {
        if (block.dataset.sdk === activeSDK) {
          block.classList.remove("hidden");
          block.style.opacity = "1";
        } else {
          block.classList.add("hidden");
          block.style.opacity = "0";
        }
      });
      this.currentSDK = activeSDK;
      return;
    }

    // Animated transition
    this.isAnimating = true;

    // Get current height
    const startHeight = outgoingBlock.offsetHeight;

    // Prepare incoming block for measurement (visible but transparent)
    incomingBlock.classList.remove("hidden");
    incomingBlock.style.opacity = "0";
    incomingBlock.style.position = "absolute";
    incomingBlock.style.visibility = "hidden";

    // Force reflow to get accurate measurement
    const endHeight = incomingBlock.offsetHeight;

    // Reset incoming block positioning
    incomingBlock.style.position = "";
    incomingBlock.style.visibility = "";

    // Set up the container for height animation
    const container = this.hasContainerTarget ? this.containerTarget : this.element;
    container.style.height = `${startHeight}px`;
    container.style.overflow = "hidden";
    container.style.transition = "height 200ms cubic-bezier(0.33, 1, 0.68, 1)";

    // Crossfade: fade out current
    outgoingBlock.style.transition = "opacity 150ms ease-out";
    outgoingBlock.style.opacity = "0";

    // After fade out, switch blocks and animate height
    setTimeout(() => {
      outgoingBlock.classList.add("hidden");
      outgoingBlock.style.transition = "";

      // Animate to new height
      container.style.height = `${endHeight}px`;

      // Fade in new block
      incomingBlock.style.transition = "opacity 150ms ease-in";
      incomingBlock.style.opacity = "1";

      // Clean up after animation
      setTimeout(() => {
        container.style.height = "";
        container.style.overflow = "";
        container.style.transition = "";
        incomingBlock.style.transition = "";
        this.isAnimating = false;
        this.currentSDK = activeSDK;
      }, 200);
    }, 150);
  }
}
